// One invoice: the page and its cards (client, lines, activity, merge, take off, pay ways, message, close, who worked, PO number).
import {
  CHECK_WAIT_DAYS,
  type InvoiceDetail,
  type InvoiceRow,
  KLARNA_MIN_CENTS,
  mergeCheck,
  PAYMENT_METHOD_WORDS,
} from "@steward/shared";
import { useNavigate } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { usePeople } from "../../api/hooks";
import {
  useInvoice,
  useMarkSent,
  useMergeInvoices,
  useNoCharge,
  useTakeVisitOff,
  useUpdateInvoice,
  useVoidInvoice,
  type Via,
} from "../../api/money";
import { useSession } from "../../app/session";
import { dayDate, followUpText, money, moneyExact, phoneText, shortDate, todayIn } from "../../components/format";
import { Menu, Modal, useToast } from "../../components/overlays";
import {
  ago,
  Banner,
  Button,
  Card,
  Details,
  IconButton,
  LoadError,
  Page,
  SettingRow,
  Skeleton,
  Switch,
} from "../../components/parts";
import { PhoneFacts, PhoneRecord } from "../../components/PhoneRecord";
import { usePhone } from "../../components/usePhone";
import { CrewChips, errText, LineMore, TeamSheet } from "../../components/work";
import { DisputeBanner } from "../DisputeFile";
import { ChargeCard, CollectPayment, EditLines, ReceiptBox, RefundBox, SendBox } from "./Boxes";
import { AutoChargeBanner, CheckMailBanner, dayIn, dueOf, InvoiceStatus, tokenOf } from "./common";

type Open = "send" | "collect" | "edit" | "charge" | "receipt" | "void" | "no_charge" | null;

export function InvoicePage({ id, then }: { id: string; then?: "send" }) {
  const [refunding, setRefunding] = useState<string | null>(null);
  const { can, company } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const inv = useInvoice(id);
  const markSent = useMarkSent();
  const update = useUpdateInvoice();
  const [open, setOpen] = useState<Open>(then === "send" ? "send" : null);
  const [via, setVia] = useState<Via>("text");
  const [phoneTab, setPhoneTab] = useState<"invoice" | "activity">("invoice");
  const phone = usePhone();
  const i = inv.data;
  if (inv.error)
    return (
      <Page title="Invoice">
        <LoadError error={inv.error} retry={() => void inv.refetch()} />
      </Page>
    );
  if (!i)
    return (
      <Page title={<span className="skeleton inline wide" />}>
        <Skeleton rows={6} />
      </Page>
    );
  const today = todayIn(company.timezone);
  const toCheck = i.state === "to_check";
  const closed = i.status === "void" || i.status === "no_charge";
  const owes = i.balanceCents > 0 && !closed;
  const card = i.client.card;
  const editable = !closed && (i.paidCents > 0 ? can("invoice.edit_paid") : can("invoice.update"));
  const done = (text: string) => ({
    onSuccess: () => toast(text),
    onError: (e: unknown) => toast(errText(e), { kind: "error" }),
  });
  const close = () => setOpen(null);
  const send = (how: Via) => {
    setVia(how);
    setOpen("send");
  };
  const due = dueOf(i, company.timezone, today);
  const details: [ReactNode, ReactNode][] = [
    ["Invoice number", i.number ? `#${String(i.number)}` : "Given when it is sent"],
    ["Issued", i.issuedOn ? shortDate(i.issuedOn) : "Not sent yet"],
    [
      "Due",
      due.late ? (
        <span className="late-words">{`${shortDate(dayIn(company.timezone, i.dueAt ?? ""))}, ${due.text}`}</span>
      ) : (
        due.text
      ),
    ],
    [
      "Last sent",
      i.sentAt ? `${shortDate(dayIn(company.timezone, i.sentAt))}${i.sentTo ? ` to ${i.sentTo}` : ""}` : "Not sent yet",
    ],
    ...(i.sentAt
      ? ([["Viewed", i.viewedAt ? `${shortDate(dayIn(company.timezone, i.viewedAt))}, ${ago(i.viewedAt)}` : "Not yet"]] as [
          string,
          string,
        ][])
      : []),
    ...(i.paidAt && i.status === "paid" ? ([["Paid", shortDate(dayIn(company.timezone, i.paidAt))]] as [string, string][]) : []),
    ...(i.visits.length ? ([["Visits", i.visits.map((v) => shortDate(v.date)).join(", ")]] as [string, string][]) : []),
    ...(i.isBusiness ? ([["PO number", <PoNumber key="po" i={i} />]] as [string, ReactNode][]) : []),
  ];
  const primary = toCheck ? (
    can("invoice.send") ? (
      <Button icon="mail" onClick={() => send("text")}>
        Confirm and send
      </Button>
    ) : null
  ) : owes && can("payment.record") ? (
    <Button icon="money" onClick={() => setOpen("collect")}>
      Collect payment
    </Button>
  ) : null;
  const sendAgain = (look: "primary" | "secondary" = "primary") =>
    !can("invoice.send") || closed || toCheck ? null : i.status === "paid" ? (
      <Button type={look} icon="mail" onClick={() => setOpen("receipt")}>
        Send receipt
      </Button>
    ) : (
      <Button type={look} icon="mail" onClick={() => send("text")}>
        Resend
      </Button>
    );
  const secondary = sendAgain("secondary");
  const moreMenu = (
    <Menu align="right" button={({ toggle }) => <IconButton icon="more" label="More" onClick={toggle} />}>
      {(closeMenu) => {
        const item = (label: string, run: () => void, show = true, danger = false) =>
          show ? (
            <button
              key={label}
              type="button"
              role="menuitem"
              className={danger ? "danger" : undefined}
              onClick={() => {
                closeMenu();
                run();
              }}
            >
              {label}
            </button>
          ) : null;
        const canSend = can("invoice.send") && !closed && i.status !== "paid";
        const canClose = i.paidCents === 0 && !closed && i.status !== "paid" && can("invoice.update");
        return (
          <>
            {canSend ? <span className="menu-label">Send as</span> : null}
            {item("Text message", () => send("text"), canSend)}
            {item("Email", () => send("email"), canSend)}
            {canSend ? <hr /> : null}
            {item("Edit line items", () => setOpen("edit"), editable)}
            {item(
              "Preview as the client sees it",
              () => void navigate({ to: "/pay/$token", params: { token: tokenOf(i.payLink) } }),
              !toCheck,
            )}
            {item(
              "Copy pay link",
              () => {
                void navigator.clipboard.writeText(i.payLink);
                toast("Pay link copied");
              },
              !toCheck,
            )}
            {item("Print or save PDF", () => window.open(`/v1/invoices/${i.id}/pdf`, "_blank"), !__PREVIEW__)}
            {item("Send receipt", () => setOpen("receipt"), i.status === "paid" && can("invoice.send"))}
            {/* A To check invoice too (owner, Oct 31): the client already OKd it, so the card on file can be charged now. */}
            {item("Charge saved card", () => setOpen("charge"), Boolean(card) && owes && can("card.charge"))}
            {item(
              "Mark as sent",
              () => markSent.mutate(i.id, done("Marked as sent. Nothing was sent to the client.")),
              toCheck && can("invoice.send"),
            )}
            {item(
              i.checkMailedAt ? "Check not coming" : "Check in the mail",
              () =>
                update.mutate(
                  { id: i.id, patch: { checkMailed: !i.checkMailedAt } },
                  done(
                    i.checkMailedAt ? "Reminders are back on" : `Reminders wait ${String(CHECK_WAIT_DAYS)} days for the check`,
                  ),
                ),
              owes && !toCheck && i.status !== "to_collect" && can("invoice.update"),
            )}
            {item(
              i.debt ? "Take out of Debt" : "Mark as debt",
              () => update.mutate({ id: i.id, patch: { debt: !i.debt } }, done(i.debt ? "Taken out of Debt" : "Marked as debt")),
              owes && !toCheck && can("invoice.update"),
            )}
            {canClose ? <hr /> : null}
            {item("No charge", () => setOpen("no_charge"), canClose)}
            {item("Void invoice", () => setOpen("void"), canClose, true)}
          </>
        );
      }}
    </Menu>
  );
  const banners = (
    <>
      <DisputeBanner invoiceId={i.id} clientId={i.clientId} />
      {i.payNote ? <Banner type="warning">{i.payNote}</Banner> : null}
      {i.checkMailedAt && owes ? <CheckMailBanner at={i.checkMailedAt} tz={company.timezone} /> : null}
      {i.status === "to_collect" && (i.autoChargeAt || i.autoChargeHeld) ? <AutoChargeBanner i={i} /> : null}
      {i.client.creditCents > 0 && owes ? (
        <Banner type="notice">
          {i.client.name} has {moneyExact(i.client.creditCents)} in account credit. It is used when this invoice is first sent.
        </Banner>
      ) : null}
    </>
  );
  const linesCard = (
    <Card
      title="Product / Service"
      action={
        editable ? (
          <Button type="tertiary" size="small" icon="edit" onClick={() => setOpen("edit")}>
            Edit
          </Button>
        ) : null
      }
      flush
    >
      <InvoiceLines i={i} />
    </Card>
  );
  const paymentsCard = (
    <>
      {refunding ? <RefundBox i={i} paymentId={refunding} onClose={() => setRefunding(null)} /> : null}
      <Card title="Payments" flush>
        {i.payments.length ? (
          <table className="data pay-rows">
            <tbody>
              {i.payments.map((p) => (
                <tr
                  key={p.id}
                  className="clickable"
                  onClick={(e) =>
                    (e.target as HTMLElement).closest("button")
                      ? undefined
                      : void navigate({ to: "/payments/$id", params: { id: p.id } })
                  }
                >
                  <td>{shortDate(dayIn(company.timezone, p.receivedAt))}</td>
                  <td>
                    {PAYMENT_METHOD_WORDS[p.method]}
                    {p.checkNumber ? ` #${p.checkNumber}` : ""}
                    {p.note ? <span className="muted"> · {p.note}</span> : null}
                  </td>
                  <td className="muted">{p.recordedBy ?? "Client"}</td>
                  <td className="right num">{moneyExact(p.amountCents)}</td>
                  <td className="right">
                    {can("card.charge") && p.amountCents > 0 ? (
                      <Button type="tertiary" size="small" onClick={() => setRefunding(p.id)}>
                        Refund
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="card-empty muted">
            {owes ? "No payments yet. Card and bank payments land here by themselves." : "No payments."}
          </div>
        )}
      </Card>
    </>
  );
  const sideCards = (
    <>
      {i.mergeWith.length && can("invoice.update") ? <MergeCard i={i} /> : null}
      {takeOffShows(i) && can("invoice.update") ? <TakeOffCard i={i} /> : null}
      {owes ? <PayWaysCard i={i} /> : null}
      {i.visits.length ? null : <WhoWorked i={i} />}
      <ClientMessage i={i} />
    </>
  );
  const sheets = (
    <>
      <SendBox key={`send-${via}`} i={i} via={via} open={open === "send"} onClose={close} />
      {open === "receipt" ? <ReceiptBox i={i} onClose={close} /> : null}
      {open === "void" || open === "no_charge" ? <CloseInvoice i={i} how={open} onClose={close} /> : null}
      <CollectPayment i={i} open={open === "collect"} onClose={close} />
      <EditLines key={`${i.id}-${String(open === "edit")}`} i={i} open={open === "edit"} onClose={close} />
      <ChargeCard i={i} open={open === "charge"} onClose={close} />
    </>
  );

  // Like Jobber's phone invoice (owner, 2026-10-30): "Invoice for X for $", Issued and Due, one big button and More,
  // Invoice and Activity tabs, payments inside the totals, the client at the bottom.
  if (phone)
    return (
      <PhoneRecord
        back={{ label: "Back", go: () => void navigate({ to: "/invoices" }) }}
        kind="invoices"
        status={
          <>
            <InvoiceStatus i={i} />
            <span className="muted num">{i.number ? `#${String(i.number)}` : null}</span>
          </>
        }
        title={`Invoice for ${i.clientName} for ${moneyExact(i.totalCents)}`}
        sub={i.subject || null}
        phone={i.client.phone}
        email={i.client.email}
        meta={[
          { label: "Issued", value: i.issuedOn ? shortDate(i.issuedOn) : "Not sent yet" },
          {
            label: "Due",
            value: due.late ? (
              <span className="late-words">{`${shortDate(dayIn(company.timezone, i.dueAt ?? ""))}, ${due.text}`}</span>
            ) : (
              due.text
            ),
          },
        ]}
        actions={
          <>
            {primary ?? sendAgain()}
            {moreMenu}
          </>
        }
        tabs={{
          tabs: [
            { key: "invoice", label: "Invoice" },
            { key: "activity", label: "Activity" },
          ],
          active: phoneTab,
          onChange: (t) => setPhoneTab(t === "activity" ? "activity" : "invoice"),
        }}
      >
        {phoneTab === "activity" ? (
          <InvoiceActivity i={i} />
        ) : (
          <>
            {banners}
            <Card
              title="Product / Service"
              action={
                editable ? (
                  <Button type="tertiary" size="small" icon="edit" onClick={() => setOpen("edit")}>
                    Edit
                  </Button>
                ) : null
              }
              flush
            >
              <InvoiceLines
                i={i}
                onPayment={(pid) => void navigate({ to: "/payments/$id", params: { id: pid } })}
                timezone={company.timezone}
              />
            </Card>
            {sideCards}
            <Card title="Overview">
              <Details rows={details} />
            </Card>
            <Card flush>
              <PhoneFacts
                facts={[
                  {
                    key: "client",
                    icon: "person",
                    main: i.clientName,
                    sub: i.client.address ?? undefined,
                    onClick: () => void navigate({ to: "/clients/$id", params: { id: i.clientId } }),
                  },
                  {
                    key: "card",
                    icon: "lock",
                    main: card ? `${card.brand} ending ${card.last4}` : "No saved card",
                    sub: [
                      i.client.autoCharge ? "Auto charge on" : "Auto charge off",
                      i.client.creditCents ? `${moneyExact(i.client.creditCents)} credit` : null,
                    ]
                      .filter(Boolean)
                      .join(" · "),
                    ...(card && owes && can("card.charge") ? { onClick: () => setOpen("charge") } : {}),
                  },
                ]}
              />
            </Card>
          </>
        )}
        {sheets}
      </PhoneRecord>
    );

  return (
    <Page
      title={
        <>
          Invoice for{" "}
          <button
            type="button"
            className="link title-link"
            onClick={() => void navigate({ to: "/clients/$id", params: { id: i.clientId } })}
          >
            {i.clientName}
          </button>{" "}
          for {moneyExact(i.totalCents)}
        </>
      }
      titleMetaData={<InvoiceStatus i={i} />}
      subtitle={i.subject || null}
      primaryAction={primary}
      secondaryAction={secondary}
      moreActions={moreMenu}
    >
      {banners}
      <div className="inv-top">
        <ClientCard i={i} />
        <Card title="Details">
          <Details rows={details} />
        </Card>
      </div>
      {linesCard}
      <div className="home-grid">
        <div className="stack">
          {paymentsCard}
          <InvoiceActivity i={i} />
        </div>
        <div className="stack">{sideCards}</div>
      </div>
      {sheets}
    </Page>
  );
}

/** Who the invoice is for, beside the details (Jobber): their address, phone, email, saved card and credit. */
function ClientCard({ i }: { i: InvoiceDetail }) {
  const navigate = useNavigate();
  const c = i.client;
  const phone = phoneText(c.phone);
  return (
    <Card
      title="Client"
      action={
        <Button type="tertiary" size="small" onClick={() => void navigate({ to: "/clients/$id", params: { id: c.id } })}>
          Open client
        </Button>
      }
    >
      <div className="stack">
        <b>{c.name}</b>
        <Details
          rows={[
            ["Address", c.address],
            ["Phone", phone && c.phone ? <a href={`tel:${c.phone}`}>{phone}</a> : null],
            ["Email", c.email ? <a href={`mailto:${c.email}`}>{c.email}</a> : null],
            ["Saved card", c.card ? `${c.card.brand} ending ${c.card.last4}` : "None"],
            ["Account credit", c.creditCents ? moneyExact(c.creditCents) : "None"],
            ["Auto charge", c.autoCharge ? "On, repeating jobs only" : "Off"],
          ]}
        />
      </div>
    </Card>
  );
}

/** Jobber's line items (name, note, quantity, unit price, total) with the old Steward's day of the work over each line. */

/** onPayment (phone): the payments sit inside the totals like Jobber's, each one opening its payment page. */
function InvoiceLines({ i, onPayment, timezone }: { i: InvoiceDetail; onPayment?: (id: string) => void; timezone?: string }) {
  return (
    <>
      <table className="data lines">
        <thead className="lines-head">
          <tr>
            <th>Line item</th>
            <th className="right">Quantity</th>
            <th className="right">Unit price</th>
            <th className="right">Total</th>
          </tr>
        </thead>
        <tbody>
          {i.lines.map((l) => (
            <tr key={l.id}>
              <td>
                {l.serviceDate || l.propertyLabel ? (
                  <div className="muted small">
                    {[l.serviceDate ? dayDate(l.serviceDate) : null, l.propertyLabel].filter(Boolean).join(" · ")}
                  </div>
                ) : null}
                <b>{l.description}</b>
                <LineMore line={l} basis={false} />
              </td>
              <td className="right num">{l.quantity}</td>
              <td className="right num muted">{moneyExact(l.unitPriceCents)}</td>
              <td className="right num">{moneyExact(l.amountCents)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="inv-totals">
        <dl>
          <div>
            <dt>Subtotal</dt>
            <dd className="num">{moneyExact(i.subtotalCents)}</dd>
          </div>
          {i.tipCents ? (
            <div>
              <dt>Tip</dt>
              <dd className="num">{moneyExact(i.tipCents)}</dd>
            </div>
          ) : null}
          <div className="total">
            <dt>Total</dt>
            <dd className="num">{moneyExact(i.totalCents)}</dd>
          </div>
          {onPayment && timezone && i.payments.length ? (
            i.payments.map((p) => (
              <div
                key={p.id}
                className="pay-line"
                role="button"
                tabIndex={0}
                onClick={() => onPayment(p.id)}
                onKeyDown={(e) => (e.key === "Enter" ? onPayment(p.id) : undefined)}
              >
                <dt>
                  {PAYMENT_METHOD_WORDS[p.method]}
                  <span className="muted"> · {shortDate(dayIn(timezone, p.receivedAt))}</span>
                </dt>
                <dd className="num">{p.amountCents < 0 ? `+${moneyExact(-p.amountCents)}` : `-${moneyExact(p.amountCents)}`}</dd>
              </div>
            ))
          ) : i.paidCents ? (
            <div>
              <dt>Paid</dt>
              <dd className="num">-{moneyExact(i.paidCents)}</dd>
            </div>
          ) : null}
        </dl>
        <div className="inv-balance">
          <div>
            <span>Invoice balance</span>
            <b className="num">{moneyExact(Math.max(0, i.balanceCents))}</b>
          </div>
          {i.client.creditCents ? (
            <div className="muted">
              <span>Account credit</span>
              <span className="num">{moneyExact(i.client.creditCents)}</span>
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}

/** What happened to the invoice, newest first, from its own dates, payments and changes (Jobber's and the old Steward's). */
function InvoiceActivity({ i }: { i: InvoiceDetail }) {
  const when = (iso: string) =>
    new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  const editWords: Record<string, string> = { credit: "credited", no_credit: "not credited", up: "raised" };
  const events: { what: string; at: string }[] = [{ what: "Made", at: i.createdAt }];
  if (i.firstSentAt)
    events.push({ what: `Sent${i.sentAt === i.firstSentAt && i.sentTo ? ` to ${i.sentTo}` : ""}`, at: i.firstSentAt });
  if (i.sentAt && i.sentAt !== i.firstSentAt)
    events.push({ what: `Sent again${i.sentTo ? ` to ${i.sentTo}` : ""}`, at: i.sentAt });
  if (i.viewedAt) events.push({ what: "Opened by the client", at: i.viewedAt });
  for (const f of i.followUps) events.push({ what: followUpText(f), at: f.at });
  for (const p of i.payments)
    events.push({
      what:
        p.amountCents < 0
          ? `${moneyExact(-p.amountCents)} given back`
          : `${moneyExact(p.amountCents)} paid by ${PAYMENT_METHOD_WORDS[p.method].toLowerCase()}${p.recordedBy ? `, written down by ${p.recordedBy}` : ""}`,
      at: p.receivedAt,
    });
  for (const e of i.edits)
    events.push({
      what: `Changed from ${moneyExact(e.fromTotalCents)} to ${moneyExact(e.toTotalCents)}${editWords[e.kind] ? `, ${editWords[e.kind] ?? ""}` : ""}${e.by ? ` by ${e.by}` : ""}`,
      at: e.at,
    });
  if (i.paidAt && i.status === "paid") events.push({ what: "Paid in full", at: i.paidAt });
  events.sort((a, b) => b.at.localeCompare(a.at));
  return (
    <Card title="Activity">
      <ul className="activity-list">
        {i.status === "void" ? (
          <li>
            <span>Voided</span>
          </li>
        ) : i.status === "no_charge" ? (
          <li>
            <span>Closed with no charge</span>
          </li>
        ) : null}
        {events.map((e, n) => (
          <li key={n}>
            <span>{e.what}</span>
            <span className="muted small num">{when(e.at)}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** Why the ticked rows on the list can't be merged (the server checks again), or null when they can. */
export function mergeHint(rows: readonly InvoiceRow[], mayEditSent: boolean): string | null {
  if (rows.length < 2) return "Tick another invoice of the same client.";
  const check = mergeCheck(
    rows.map((r) => ({
      id: r.id,
      clientId: r.clientId,
      status: r.status,
      paidCents: r.paidCents,
      billKind: null,
      quoteId: null,
    })),
    mayEditSent,
  );
  return check.ok ? null : check.message;
}

/**
 * The old Steward's "Put it on Sydney's other invoice" and Add to invoice. From To check, the client's other To check
 * invoices and a sent one not paid yet; from a sent one, the To check ones. Nothing is sent.
 */
function MergeCard({ i }: { i: InvoiceDetail }) {
  const { can } = useSession();
  const merge = useMergeInvoices();
  const navigate = useNavigate();
  const toast = useToast();
  const mayEditSent = can("invoice.edit_paid");
  if (i.status === "sent" && !mayEditSent) return null;
  const options = i.mergeWith.filter((o) => o.status !== "sent" || mayEditSent);
  if (!options.length) return null;
  const many = options.length > 1;
  const sentHere = i.status === "sent";
  return (
    <Card
      title={
        sentHere
          ? `Add ${i.client.firstName}'s waiting ${many ? "invoices" : "invoice"}`
          : `Put it on ${i.client.firstName}'s other ${many ? "invoices" : "invoice"}`
      }
    >
      <div className="stack">
        <p className="muted small">
          The lines go on one invoice, each with its own day. A sent invoice keeps its number and pay link and shows the new
          total. The other one is voided. Nothing is sent.
        </p>
        {options.map((o) => (
          <div className="row between" key={o.id}>
            <span>
              {o.status === "sent" ? <b>{o.number ? `#${String(o.number)} sent, ` : "Sent, "}</b> : null}
              {o.firstServiceDate ? dayDate(o.firstServiceDate) : "No day"}, {moneyExact(o.totalCents)}
            </span>
            <Button
              type="secondary"
              size="small"
              loading={merge.isPending && merge.variables.includes(o.id)}
              onClick={() =>
                merge.mutate([i.id, o.id], {
                  onSuccess: (d) => {
                    toast(o.status === "sent" || sentHere ? "Added to the sent invoice" : "Put on one invoice");
                    if (d.id !== i.id) void navigate({ to: "/invoices/$id", params: { id: d.id } });
                  },
                  onError: (e) => toast(errText(e), { kind: "error" }),
                })
              }
            >
              {o.status === "sent" ? "Add to it" : sentHere ? "Add here" : "Put together"}
            </Button>
          </div>
        ))}
      </div>
    </Card>
  );
}

/** A visit can come off: more than one visit, in To check or sent and not paid. */
function takeOffShows(i: InvoiceDetail): boolean {
  return i.visits.length > 1 && (i.status === "to_check" || i.status === "sent");
}

/** The old Steward's unmerge: one visit comes off onto a new invoice in To check. Nothing is sent. */
function TakeOffCard({ i }: { i: InvoiceDetail }) {
  const { can } = useSession();
  const off = useTakeVisitOff();
  const navigate = useNavigate();
  const toast = useToast();
  if (i.status === "sent" && !can("invoice.edit_paid")) return null;
  return (
    <Card title="Take a visit off">
      <div className="stack">
        <p className="muted small">Its lines go on a new invoice in To check. This one shows the lower total. Nothing is sent.</p>
        {i.visits.map((v) => (
          <div className="row between" key={v.ref}>
            <span>
              {dayDate(v.date)}, {v.jobName}
            </span>
            <Button
              type="tertiary"
              size="small"
              loading={off.isPending && off.variables.visitId === v.ref}
              onClick={() =>
                off.mutate(
                  { id: i.id, visitId: v.ref },
                  {
                    onSuccess: (d) => {
                      toast("Taken off. It has its own invoice now");
                      void navigate({ to: "/invoices/$id", params: { id: d.id } });
                    },
                    onError: (e) => toast(errText(e), { kind: "error" }),
                  },
                )
              }
            >
              Take off
            </Button>
          </div>
        ))}
      </div>
    </Card>
  );
}

/** The old Steward's "How they can pay", for this invoice only: card always, bank and Klarna on or off. */
function PayWaysCard({ i }: { i: InvoiceDetail }) {
  const { can } = useSession();
  const update = useUpdateInvoice();
  const toast = useToast();
  const w = i.payWays;
  const edit = can("invoice.update");
  const save = (payWays: { card: true; bank: boolean; klarna: boolean } | null) =>
    update.mutate(
      { id: i.id, patch: { payWays } },
      {
        onSuccess: () => toast(payWays ? "Saved for this invoice" : "Back to your settings"),
        onError: (e) => toast(errText(e), { kind: "error" }),
      },
    );
  return (
    <Card
      title="How they can pay"
      action={
        w.own && edit ? (
          <Button type="tertiary" size="small" loading={update.isPending} onClick={() => save(null)}>
            Use my settings
          </Button>
        ) : null
      }
    >
      <div className="stack">
        <SettingRow
          title="Card"
          description="Always on, with Apple Pay and Google Pay"
          control={<Switch checked label="Card" disabled onChange={() => undefined} />}
        />
        <SettingRow
          title="Bank (ACH)"
          description="Takes a few days to land"
          control={
            <Switch
              checked={w.bank}
              label="Bank (ACH)"
              disabled={!edit || update.isPending}
              onChange={(bank) => save({ card: true, bank, klarna: w.klarna })}
            />
          }
        />
        <SettingRow
          title="Pay in 4 with Klarna"
          description={
            i.klarnaFits
              ? "Shows on this invoice"
              : `Only for one-time work of ${money(KLARNA_MIN_CENTS)} or more that is not charged by itself`
          }
          control={
            <Switch
              checked={w.klarna && i.klarnaFits}
              label="Pay in 4 with Klarna"
              disabled={!edit || !i.klarnaFits || update.isPending}
              onChange={(klarna) => save({ card: true, bank: w.bank, klarna })}
            />
          }
        />
        <p className="muted small">
          {w.own
            ? "Set for this invoice only. Your settings stay the same."
            : "From your settings. A change here is for this invoice only."}
        </p>
      </div>
    </Card>
  );
}

/** The message on the invoice (the old Steward's Client message): read first, Edit to change it while it is not paid. */
function ClientMessage({ i }: { i: InvoiceDetail }) {
  const { can } = useSession();
  const update = useUpdateInvoice();
  const toast = useToast();
  const [writing, setWriting] = useState(false);
  const [text, setText] = useState(i.noteToClient ?? "");
  const edit = can("invoice.update") && i.status !== "paid" && i.status !== "void" && i.status !== "no_charge";
  if (!i.noteToClient && !edit) return null;
  return (
    <Card
      title="Message to the client"
      action={
        edit && !writing ? (
          <Button type="tertiary" size="small" icon="edit" onClick={() => setWriting(true)}>
            {i.noteToClient ? "Edit" : "Add"}
          </Button>
        ) : null
      }
    >
      {writing ? (
        <div className="stack">
          <textarea
            rows={3}
            value={text}
            aria-label="Message to the client"
            placeholder="Thanks for having us. Call us with any questions."
            onChange={(e) => setText(e.target.value)}
          />
          <div className="row">
            <Button
              size="small"
              loading={update.isPending}
              onClick={() =>
                update.mutate(
                  { id: i.id, patch: { noteToClient: text.trim() || null } },
                  {
                    onSuccess: () => {
                      setWriting(false);
                      toast("Message saved");
                    },
                    onError: (e) => toast(errText(e), { kind: "error" }),
                  },
                )
              }
            >
              Save
            </Button>
            <Button
              type="tertiary"
              size="small"
              onClick={() => {
                setText(i.noteToClient ?? "");
                setWriting(false);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : i.noteToClient ? (
        <p className="pre-wrap">{i.noteToClient}</p>
      ) : (
        <p className="muted">None. It shows on the invoice and the pay page.</p>
      )}
    </Card>
  );
}

/** Void or No charge, asked once in a popup (money rule: a confirm before it closes). */
function CloseInvoice({ i, how, onClose }: { i: InvoiceDetail; how: "void" | "no_charge"; onClose: () => void }) {
  const voidIt = useVoidInvoice();
  const noCharge = useNoCharge();
  const toast = useToast();
  const busy = voidIt.isPending || noCharge.isPending;
  const after = {
    onSuccess: () => {
      toast(how === "void" ? "Invoice voided" : "Closed with no charge");
      onClose();
    },
    onError: (e: unknown) => toast(errText(e), { kind: "error" }),
  };
  return (
    <Modal
      title={how === "void" ? "Void this invoice?" : "Close with no charge?"}
      open
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            {...(how === "void" ? { variation: "destructive" as const } : {})}
            loading={busy}
            onClick={() => (how === "void" ? voidIt.mutate(i.id, after) : noCharge.mutate(i.id, after))}
          >
            {how === "void" ? "Void invoice" : "No charge"}
          </Button>
        </>
      }
    >
      <p>
        {how === "void"
          ? `${i.clientName} no longer owes ${moneyExact(i.balanceCents)}. The pay link stops working. Use it for an invoice made by mistake.`
          : `${i.clientName} owes nothing for this work. It stays on their history as no charge.`}
      </p>
    </Modal>
  );
}

/**
 * Who worked an invoice typed by hand (no visits), so payroll pays them (question 43). Invoices from visits get their team
 * from each visit.
 */
function WhoWorked({ i }: { i: InvoiceDetail }) {
  const { can, company } = useSession();
  const people = usePeople(company.id);
  const update = useUpdateInvoice();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const editable = can("invoice.update") && i.status !== "void" && i.status !== "no_charge";
  const team = (people.data ?? []).filter((p) => p.active).map((p) => ({ id: p.id, name: p.name, hint: p.role }));
  return (
    <Card
      title="Who worked it"
      action={
        editable ? (
          <Button type="tertiary" size="small" icon="person" onClick={() => setOpen(true)}>
            {i.crew.length ? "Change" : "Set team"}
          </Button>
        ) : null
      }
    >
      <CrewChips
        crew={i.crew.map((c) => ({ ...c, color: null, photo: null }))}
        none={i.nobodyWorked ? "Nobody worked this" : "Nobody yet"}
      />
      {editable && i.crew.length === 0 ? (
        <label className="check-row">
          <input
            type="checkbox"
            checked={i.nobodyWorked}
            onChange={(e) => update.mutate({ id: i.id, patch: { nobodyWorked: e.target.checked } })}
          />
          Nobody worked this (a fee or a product). Payroll pays nobody and it leaves No crew yet.
        </label>
      ) : null}
      <p className="muted">
        {i.crew.length
          ? "Payroll pays them for this invoice once it is paid."
          : "This invoice has no visit, so payroll pays nobody for it until you set who worked it."}
      </p>
      <TeamSheet
        open={open}
        onClose={() => setOpen(false)}
        people={team}
        selected={i.crew.map((c) => c.id)}
        repeating={false}
        saving={update.isPending}
        onSave={(ids) =>
          update.mutate(
            { id: i.id, patch: { crew: ids } },
            {
              onSuccess: () => {
                setOpen(false);
                toast("Team saved");
              },
              onError: (e) => toast(errText(e), { kind: "error" }),
            },
          )
        }
      />
    </Card>
  );
}

function PoNumber({ i }: { i: InvoiceDetail }) {
  const { can } = useSession();
  const update = useUpdateInvoice();
  const toast = useToast();
  const [po, setPo] = useState(i.poNumber ?? "");
  if (!can("invoice.update") || i.status === "paid") return i.poNumber ?? <span className="muted">None</span>;
  return (
    <form
      className="row"
      onSubmit={(e) => {
        e.preventDefault();
        update.mutate(
          { id: i.id, patch: { poNumber: po.trim() || null } },
          { onSuccess: () => toast("PO number saved"), onError: (er) => toast(errText(er), { kind: "error" }) },
        );
      }}
    >
      <input
        className="input-plain"
        value={po}
        placeholder="Add one"
        aria-label="PO number"
        onChange={(e) => setPo(e.target.value)}
      />
      {po !== (i.poNumber ?? "") ? (
        <Button size="small" submit loading={update.isPending}>
          Save
        </Button>
      ) : null}
    </form>
  );
}

/** Shows the exact message and who gets it before anything goes out. */
